import { useState } from 'react';
import { Trans } from '@lingui/react/macro';

import Button from '@/design_system/Button/Button';
import Message from '@/design_system/Message';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import IconAdd from '@/icons/Add.svg';
import { computeDuplicateDefectNumber } from '@/models/defectType';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import useViewPort from '@/utils/useViewport';

import { DefectCard } from './DefectCard/DefectCard';
import { CreateDefectDialogs } from './CreateDefectDialogs';
import { DefectTypeSearchSelect } from './DefectTypeSearchSelect';

export const DefectsList = ({
  showOnlyDefectsNotAssociatedToInitialCondition,
}: {
  showOnlyDefectsNotAssociatedToInitialCondition?: boolean;
}) => {
  const { isMobile } = useViewPort();

  const [showAddDefectOptions, setShowAddDefectOptions] = useState(false);

  const { article, view } = useArticleContext();

  const defects = article.snapshot.articleDefects.filter((articleDefect) => {
    if (showOnlyDefectsNotAssociatedToInitialCondition) {
      return !articleDefect.articleConditionId;
    }

    return true;
  });

  const previousDefects =
    article.previousSnapshot?.articleDefects.filter((articleDefect) => {
      if (showOnlyDefectsNotAssociatedToInitialCondition) {
        return !articleDefect.articleConditionId;
      }

      return true;
    }) ?? [];

  const deletedDefects = previousDefects.filter(
    (prevDefect) => !defects.some((defect) => prevDefect.stableId === defect.stableId)
  );

  return (
    <Stack gap="0.25rem">
      <Stack gap="0.5rem">
        {[...previousDefects, ...defects].length === 0 ? (
          <InlineText color="secondary">
            <Trans id="defects-list.no-defect-added">No defects added</Trans>
          </InlineText>
        ) : (
          <>
            {deletedDefects.map((previousDefect) => (
              <DefectCard key={previousDefect.id} previousDefect={previousDefect} />
            ))}
            {defects.map((defect) => (
              <DefectCard
                key={defect.id}
                defect={defect}
                previousDefect={previousDefects.find(
                  (previousDefect) => defect.stableId === previousDefect.stableId
                )}
                duplicateDefectNumber={computeDuplicateDefectNumber(
                  defect,
                  article.snapshot.articleDefects
                )}
              />
            ))}
          </>
        )}

        {view.defects.canAddRemoveDefect &&
          (isMobile ? (
            <>
              <AddDefectButton setShowAddDefectOptions={setShowAddDefectOptions} />
              <CreateDefectDialogs
                isOpen={showAddDefectOptions}
                setIsOpen={setShowAddDefectOptions}
              />
            </>
          ) : (
            <>
              {!showAddDefectOptions ? (
                <AddDefectButton setShowAddDefectOptions={setShowAddDefectOptions} />
              ) : (
                <DefectTypeSearchSelect onBlur={() => setShowAddDefectOptions(false)} />
              )}
            </>
          ))}
      </Stack>

      <DefectsListErrors />
    </Stack>
  );
};

const AddDefectButton = ({
  setShowAddDefectOptions,
}: {
  setShowAddDefectOptions: (show: boolean) => void;
}) => {
  return (
    <Button
      size="medium"
      variant="secondary"
      style={{ alignSelf: 'flex-start' }}
      onPress={() => setShowAddDefectOptions(true)}
    >
      <IconAdd />
      <Trans id="defects-list.footer.add-defect">Add a defect</Trans>
    </Button>
  );
};

const DefectsListErrors = () => {
  const { errors } = useArticleContext();

  const listErrors = [];

  if (errors.defects?.noDefects) {
    listErrors.push(
      <Trans id="defects-list.errors.no-defects">Please select at least one defect.</Trans>
    );
  }

  if (errors.defects?.defects?.some((defect) => defect.missingDescription)) {
    listErrors.push(
      <Trans id="defects-list.errors.missing-description">
        Please add a description for each defect.
      </Trans>
    );
  }

  if (errors.defects?.defects?.some((defect) => defect.missingPhotos)) {
    listErrors.push(
      <Trans id="defects-list.errors.missing-photos">
        Please add at least one photo per defect.
      </Trans>
    );
  }

  if (errors.defects?.defects?.some((defect) => defect.missingActions)) {
    listErrors.push(
      <Trans id="defects-list.errors.missing-actions">
        Please associate an action to each defect or mark them as not to be repaired.
      </Trans>
    );
  }

  if (errors.defects?.defects?.some((defect) => defect.missingContext)) {
    listErrors.push(
      <Trans id="defects-list.errors.missing-context">Please add context for each defect.</Trans>
    );
  }

  if (errors.defects?.defects?.some((defect) => defect.missingExceptionalCoverageReason)) {
    listErrors.push(
      <Trans id="defects-list.errors.missing-exceptional-coverage-reason">
        Please select a reason for exceptional brand coverage for each defect.
      </Trans>
    );
  }

  if (listErrors.length === 0) {
    return null;
  }

  return (
    <Stack gap="0.25rem" style={{ paddingTop: '0.5rem' }}>
      {listErrors.map((error, index) => (
        <Message key={index} type="error">
          {error}
        </Message>
      ))}
    </Stack>
  );
};
